import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface Category {
  _id: string;
  name: string;
  nameArabic?: string;
  slug: string;
  type: 'main' | 'sub';
  active: boolean;
  order: number;
  description?: string;
  descriptionArabic?: string;
  icon?: string;
  parentCategory?: string;
  parentCategories?: string[];
}

export default function SubcategoriesTable() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = i18n.language === 'ar';
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [mainCategories, setMainCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState(''); // The search term actually used for API calls
  const [parentFilter, setParentFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    loadMainCategories();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [activeSearch, parentFilter, activeFilter]);

  useEffect(() => {
    loadSubcategories();
  }, [currentPage, limit, activeSearch, parentFilter, activeFilter]);

  const loadSubcategories = async () => {
    try {
      setLoading(true);
      const params: any = { 
        type: 'sub', 
        page: currentPage,
        limit: limit,
      };
      
      if (activeSearch) params.search = activeSearch;
      if (activeFilter === 'active') params.activeOnly = 'true';
      if (activeFilter === 'inactive') params.activeOnly = 'false';
      
      const response = await api.get('/admin/categories', { params });
      let data = response.data.data || [];
      
      // Filter by parent category on frontend if needed
      if (parentFilter) {
        data = data.filter((sub: Category) => {
          const parentId = (sub.parentCategory as any)?._id || sub.parentCategory;
          return parentId === parentFilter;
        });
      }
      
      setSubcategories(data);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMainCategories = async () => {
    try {
      // Use admin endpoint so response has { data: [...] }; public /categories returns the array directly
      const response = await api.get('/admin/categories', {
        params: { type: 'main', limit: 200, page: 1, activeOnly: 'false' },
      });
      const mainCats = response.data.data || [];
      setMainCategories(mainCats);
    } catch (error) {
      console.error('Error loading main categories:', error);
    }
  };

  const handleSearch = () => {
    setActiveSearch(search);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('subcategories.confirmDelete'))) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      loadSubcategories();
    } catch (error: any) {
      console.error('Error deleting subcategory:', error);
      alert(error.response?.data?.message || t('subcategories.failedToDelete'));
    }
  };


  if (loading) {
    return <div className="text-center py-8">{t('subcategories.loading')}</div>;
  }

  return (
    <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className={`flex flex-col md:flex-row gap-4 ${isRtl ? 'md:flex-row-reverse' : ''}`}>
          <div className="flex-1 relative flex items-center">
            <MagnifyingGlassIcon className={`absolute w-5 h-5 text-gray-400 ${isRtl ? 'right-3 left-auto' : 'left-3'}`} />
            <input
              type="text"
              placeholder={t('subcategories.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              className={`w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent ${isRtl ? 'pr-10 pl-20' : 'pl-10 pr-20'}`}
            />
            <button
              onClick={handleSearch}
              className={`absolute px-4 py-1.5 bg-primary-orange hover:bg-orange-600 text-white rounded-lg text-sm transition-colors ${isRtl ? 'left-2 right-auto' : 'right-2'}`}
            >
              {t('common.search')}
            </button>
          </div>
          <div className={`flex gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <select
              value={parentFilter}
              onChange={(e) => setParentFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
            >
              <option value="">{t('subcategories.allParents')}</option>
              {mainCategories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
            
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
            >
              <option value="all">{t('subcategories.allStatus')}</option>
              <option value="active">{t('subcategories.active')}</option>
              <option value="inactive">{t('subcategories.inactive')}</option>
            </select>
            <button
              onClick={() => navigate('/subcategories/new')}
              className="bg-primary-orange hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              + {t('subcategories.addSubcategory')}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('subcategories.name')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('categories.arabicName')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('subcategories.parentCategory')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('categories.slug')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('subcategories.order')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('subcategories.status')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>{t('subcategories.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {subcategories.map((subcategory) => (
                <tr key={subcategory._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                      {subcategory.icon && <span className={isRtl ? 'ml-2 mr-0' : 'mr-2'}>{subcategory.icon}</span>}
                      <div className="text-sm font-medium text-gray-900">{subcategory.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {subcategory.nameArabic || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {(subcategory.parentCategory as any)?.name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {subcategory.slug}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {subcategory.order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        subcategory.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {subcategory.active ? t('subcategories.active') : t('subcategories.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className={`flex flex-wrap items-center gap-3 ${isRtl ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                      <button
                        onClick={() => navigate(`/subcategories/${subcategory._id}/edit`)}
                        className="text-primary-orange hover:text-orange-600"
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => handleDelete(subcategory._id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {subcategories.length === 0 && (
          <div className="text-center py-8 text-gray-500">{t('subcategories.noSubcategories')}</div>
        )}
        {totalPages > 1 && (
          <div className={`px-6 py-4 border-t border-gray-200 flex items-center justify-between ${isRtl ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <span className="text-sm text-gray-700">
                {t('common.showing', {
                  from: ((currentPage - 1) * limit) + 1,
                  to: Math.min(currentPage * limit, total),
                  total,
                })}
              </span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
              >
                <option value={10}>10 {t('common.perPage')}</option>
                <option value={20}>20 {t('common.perPage')}</option>
                <option value={50}>50 {t('common.perPage')}</option>
                <option value={100}>100 {t('common.perPage')}</option>
              </select>
            </div>
            <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('common.previous')}
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        currentPage === pageNum
                          ? 'bg-primary-orange text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
